import CreateUpdateVat from "../components/CreateUpdateVat";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Space, Switch, notification } from "antd";
import useVatsApi from "./useVatsApi";
import RemoveButton from "../components/RemoveButton";
import useNotification from "utils/hooks/useNotification";
import type { ApolloQueryResult } from "@apollo/client";

type VatsRefetch = (
  variables?: Partial<ListVatsVars> | undefined,
) => Promise<ApolloQueryResult<ListVatsResponse>>;

/** A VAT row as fed to the table (the row adds antd's `key`). */
interface VatTableRow extends ListVatsData {
  key?: ID;
}

export default function useAccountingTableColumns(_dataSource: unknown, refetch: VatsRefetch) {
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();

  const { activateVat } = useVatsApi(urlParams.clientId);
  const { notificationSuccessfullySaved } = useNotification();

  const handleOnChangeSwitch = async (checked: boolean, record: VatTableRow) => {
    const response = await activateVat({
      variables: {
        input: {
          companyId: urlParams?.companyId,
          active: checked,
          vatId: record?.id,
        },
      },
    });

    const errors = response?.data?.activateVat?.errors;

    if ((errors?.length ?? 0) > 0) {
      errors?.forEach((error: { field: string; messages: string[] }) => {
        error?.messages?.forEach((message: string) => {
          notification.error({
            message: t("TVA"),
            description: message,
            placement: "bottomLeft",
          });
        });
      });
      refetch();
    } else {
      refetch();
      notificationSuccessfullySaved(t("VAT"));
    }
  };

  return [
    {
      title: t("Code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("Rate"),
      dataIndex: "ratePercentage",
      key: "ratePercentage",
      sorter: true,
      render: (_: unknown, record: VatTableRow) => {
        return <span>{record.ratePercentage} %</span>;
      },
    },
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("Active"),
      key: "active",
      align: "center",
      width: "100px",
      render: (_text: unknown, record: VatTableRow) => {
        return (
          <Switch
            checked={record?.active}
            onChange={checked => handleOnChangeSwitch(checked, record)}
            key={record.id}
          />
        );
      },
    },
    {
      dataIndex: t("Action"),
      key: "action",
      title: "Action",
      align: "center",
      width: "100px",
      render: (_text: unknown, record: VatTableRow) => {
        return (
          <>
            {record?.editable && (
              <Space size="small" direction="horizontal" className="centered">
                <CreateUpdateVat id={record.id} refetch={refetch} />
                <RemoveButton id={record.id} refetch={refetch} />
              </Space>
            )}
          </>
        );
      },
    },
  ];
}
